import * as React from 'react';
import { X } from 'lucide-react';

import { FormError } from '@/components/ui/form-field';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

export type SimpleSelectOption = {
  value: string | number;
  label: string;
};

// Permissive setter signature compatible with both Formik's setFieldValue
// (Promise + value: any) AND narrow ad-hoc handlers like
// `(field, value: string) => void`. The `any` here is load-bearing: a
// narrower type breaks call sites that pass string-typed handlers because
// function-parameter contravariance prevents them from accepting `unknown`.
// Banked: a project-wide migration to a single typed handler shape would
// let this become `(field: string, value: unknown, additionalData?: unknown) => unknown`.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SetValueFn = (field: string, value: any, additionalData?: any) => unknown;

export interface SimpleSelectProps {
  options?: SimpleSelectOption[];
  value?: SimpleSelectOption | string | number | null | '';
  name?: string;
  controlId?: string;
  ariaDescribedBy?: string;
  placeholder?: string;
  label?: React.ReactNode;
  isRequired?: boolean;
  errors?: string | boolean | null;
  disabled?: boolean;
  maxMenuHeight?: number;
  isClearable?: boolean;
  setFieldValue?: SetValueFn;
  field?: string;
  /** Forwarded to setFieldValue's 3rd arg; opaque to this component. */
  additionalData?: unknown;
  className?: string;
  md?: string;
}

const widthClassFromMd = (md?: string): string => {
  if (!md) return '';
  const map: Record<string, string> = {
    '1': 'w-1/12',
    '2': 'w-2/12',
    '3': 'w-3/12',
    '4': 'w-4/12',
    '5': 'w-5/12',
    '6': 'w-6/12',
    '7': 'w-7/12',
    '8': 'w-8/12',
    '9': 'w-9/12',
    '10': 'w-10/12',
    '11': 'w-11/12',
    '12': 'w-full',
  };
  return map[md] ?? '';
};

const normalizeValue = (
  value: SimpleSelectProps['value']
): string | undefined => {
  if (value == null || value === '') return undefined;
  if (typeof value === 'object') return String(value.value);
  return String(value);
};

export type SimpleSelectHandle = {
  /** Resets the displayed selection. Does not propagate via setFieldValue —
   * consumers that wire `clearValue` are expected to manage their own
   * external state (filter store, Formik values) and call this for the
   * visual reset only. */
  clearValue: () => void;
};

/**
 * One-prop convenience wrapper over Select for flat `{label, value}` option lists.
 *
 * @category Forms
 */
export const SimpleSelect = React.forwardRef<
  SimpleSelectHandle,
  SimpleSelectProps
>(function SimpleSelect(
  {
    options,
    value,
    name,
    controlId,
    ariaDescribedBy,
    placeholder,
    label,
    isRequired,
    errors,
    disabled,
    maxMenuHeight,
    isClearable,
    setFieldValue,
    field,
    additionalData,
    className,
    md,
  },
  ref
) {
  const widthClass = widthClassFromMd(md);
  const triggerId = controlId ? `htmlFor_${controlId}` : undefined;
  const errorMessage = typeof errors === 'string' ? errors : null;
  const hasError = Boolean(errors);

  const safeOptions = options ?? [];
  const normalizedValue = normalizeValue(value);

  // Internal display state — mirrors the legacy SearchSelectUI contract this
  // component replaced. Many consumers (e.g. usePropertyForm) only update the
  // `value` prop in edit-mode hydration, not on every user selection, and rely
  // on the component to render the selection optimistically.
  const [internalValue, setInternalValue] = React.useState<string | undefined>(
    normalizedValue
  );
  React.useEffect(() => {
    setInternalValue(normalizedValue);
  }, [normalizedValue]);

  React.useImperativeHandle(ref, () => ({
    clearValue: () => setInternalValue(undefined),
  }), []);

  const showClearButton = Boolean(
    isClearable && internalValue && !disabled
  );

  const handleValueChange = (newValue: string) => {
    setInternalValue(newValue);
    if (!setFieldValue || !field) return;
    const matched = safeOptions.find((o) => String(o.value) === newValue);
    setFieldValue(field, matched ? matched.value : newValue, additionalData);
  };

  const handleClear = (e: React.MouseEvent | React.PointerEvent) => {
    // Prevent the click/pointer from propagating to the SelectTrigger and
    // re-opening the dropdown.
    e.preventDefault();
    e.stopPropagation();
    setInternalValue(undefined);
    if (setFieldValue && field) {
      setFieldValue(field, '', additionalData);
    }
  };

  return (
    <div className={cn(widthClass, className)}>
      {label && (
        <Label htmlFor={triggerId} required={isRequired}>
          {label}
        </Label>
      )}
      <div className="relative">
        <Select
          value={internalValue ?? ''}
          onValueChange={handleValueChange}
          disabled={disabled}
          name={name}
        >
          <SelectTrigger
            id={triggerId}
            aria-describedby={ariaDescribedBy}
            aria-invalid={hasError || undefined}
            className={cn(
              hasError && 'border-destructive',
              showClearButton && 'pr-12'
            )}
          >
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent
            style={maxMenuHeight ? { maxHeight: maxMenuHeight } : undefined}
          >
            {safeOptions.map((o) => (
              <SelectItem key={String(o.value)} value={String(o.value)}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {showClearButton && (
          <button
            type="button"
            aria-label="Clear Selection"
            onPointerDown={handleClear}
            onClick={handleClear}
            className="absolute right-8 top-1/2 -translate-y-1/2 inline-flex h-5 w-5 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <FormError message={errorMessage} />
    </div>
  );
});
